export interface RegentsQuestion {
  id: number;
  type: 'multiple-choice' | 'short-answer' | 'extended';
  points: number;
  question: string;
  options?: string[];
  correctAnswer: number | string;
  solution: string[];
  topic: string;
  standard: string;
}

export const regentsTest1: RegentsQuestion[] = [
  {
    id: 1,
    type: 'multiple-choice',
    points: 2,
    topic: 'Linear Equations',
    standard: 'A-REI.B.3',
    question: 'What is the solution to 3x − 7 = 14?',
    options: ['x = 3', 'x = 7', 'x = 9', 'x = 21'],
    correctAnswer: 1,
    solution: [
      'Add 7 to both sides: 3x − 7 + 7 = 14 + 7',
      '3x = 21',
      'Divide both sides by 3: x = 21 ÷ 3',
      'x = 7'
    ]
  },
  {
    id: 2,
    type: 'multiple-choice',
    points: 2,
    topic: 'Polynomials',
    standard: 'A-APR.A.1',
    question: 'Which expression is equivalent to (3x² + 5x − 2) − (x² − 3x + 4)?',
    options: ['2x² + 8x − 6', '2x² + 2x + 2', '4x² + 2x − 6', '2x² + 8x + 2'],
    correctAnswer: 0,
    solution: [
      'Distribute the negative sign: 3x² + 5x − 2 − x² + 3x − 4',
      'Combine x² terms: 3x² − x² = 2x²',
      'Combine x terms: 5x + 3x = 8x',
      'Combine constants: −2 − 4 = −6',
      'Answer: 2x² + 8x − 6'
    ]
  },
  {
    id: 3,
    type: 'multiple-choice',
    points: 2,
    topic: 'Functions',
    standard: 'F-IF.A.2',
    question: 'If f(x) = 2x² − 3x + 1, what is f(−2)?',
    options: ['3', '11', '15', '−3'],
    correctAnswer: 1,
    solution: [
      'Substitute x = −2: f(−2) = 2(−2)² − 3(−2) + 1',
      'Evaluate (−2)² = 4: f(−2) = 2(4) − 3(−2) + 1',
      'Multiply: f(−2) = 8 + 6 + 1',
      'f(−2) = 15'
    ]
  },
  {
    id: 4,
    type: 'multiple-choice',
    points: 2,
    topic: 'Factoring',
    standard: 'A-SSE.B.3',
    question: 'Which is the completely factored form of x² − 5x − 14?',
    options: ['(x − 7)(x + 2)', '(x + 7)(x − 2)', '(x − 14)(x + 1)', '(x − 2)(x + 7)'],
    correctAnswer: 0,
    solution: [
      'Find two numbers that multiply to −14 and add to −5',
      'Test: −7 × 2 = −14 ✓ and −7 + 2 = −5 ✓',
      'Write the factored form: (x − 7)(x + 2)',
      'Check: (x − 7)(x + 2) = x² + 2x − 7x − 14 = x² − 5x − 14 ✓'
    ]
  },
  {
    id: 5,
    type: 'multiple-choice',
    points: 2,
    topic: 'Systems of Equations',
    standard: 'A-REI.C.6',
    question: 'What is the solution to the system: y = 2x + 1 and y = −x + 7?',
    options: ['(2, 5)', '(3, 4)', '(1, 3)', '(4, 3)'],
    correctAnswer: 0,
    solution: [
      'Set the equations equal: 2x + 1 = −x + 7',
      'Add x to both sides: 3x + 1 = 7',
      'Subtract 1: 3x = 6',
      'Divide by 3: x = 2',
      'Substitute into y = 2x + 1: y = 2(2) + 1 = 5',
      'Solution: (2, 5)'
    ]
  },
  {
    id: 6,
    type: 'multiple-choice',
    points: 2,
    topic: 'Quadratic Equations',
    standard: 'A-REI.B.4',
    question: 'What are the solutions to x² − 9 = 0?',
    options: ['x = 3 only', 'x = −3 only', 'x = 3 or x = −3', 'x = 9 or x = −9'],
    correctAnswer: 2,
    solution: [
      'Add 9 to both sides: x² = 9',
      'Take the square root of both sides: x = ±√9',
      'Simplify: x = ±3',
      'Solutions: x = 3 or x = −3'
    ]
  },
  {
    id: 7,
    type: 'multiple-choice',
    points: 2,
    topic: 'Linear Functions',
    standard: 'F-IF.B.6',
    question: 'What is the slope of the line passing through (1, 3) and (4, 12)?',
    options: ['2', '3', '4', '9'],
    correctAnswer: 1,
    solution: [
      'Use the slope formula: m = (y₂ − y₁) / (x₂ − x₁)',
      'Substitute: m = (12 − 3) / (4 − 1)',
      'm = 9 / 3',
      'm = 3'
    ]
  },
  {
    id: 8,
    type: 'multiple-choice',
    points: 2,
    topic: 'Inequalities',
    standard: 'A-REI.B.3',
    question: 'Which value of x satisfies −2x + 5 > 11?',
    options: ['x = −4', 'x = 0', 'x = 3', 'x = −2'],
    correctAnswer: 0,
    solution: [
      'Subtract 5 from both sides: −2x > 6',
      'Divide by −2 and flip the inequality: x < −3',
      'Check x = −4: −2(−4) + 5 = 8 + 5 = 13 > 11 ✓',
      'x = −4 satisfies the inequality'
    ]
  },
  {
    id: 9,
    type: 'multiple-choice',
    points: 2,
    topic: 'Exponential Functions',
    standard: 'F-LE.A.1',
    question: 'A population doubles every year. If it starts at 500, which function models the population P after t years?',
    options: ['P = 500 + 2t', 'P = 500 · 2t', 'P = 500 · 2^t', 'P = 2 · 500^t'],
    correctAnswer: 2,
    solution: [
      'Doubling means multiplying by 2 each year — this is exponential growth',
      'The initial value is 500',
      'The growth factor is 2 (doubles each year)',
      'Exponential model: P = 500 · 2^t'
    ]
  },
  {
    id: 10,
    type: 'multiple-choice',
    points: 2,
    topic: 'Rational Exponents',
    standard: 'N-RN.A.2',
    question: 'Which expression is equivalent to 27^(2/3)?',
    options: ['3', '6', '9', '18'],
    correctAnswer: 2,
    solution: [
      'Rewrite using the definition: 27^(2/3) = (³√27)²',
      'Evaluate the cube root: ³√27 = 3',
      'Square the result: 3² = 9',
      'Answer: 9'
    ]
  },
  {
    id: 11,
    type: 'multiple-choice',
    points: 2,
    topic: 'Statistics',
    standard: 'S-ID.A.2',
    question: 'The data set is: 4, 7, 7, 9, 13. What is the mean?',
    options: ['7', '8', '9', '10'],
    correctAnswer: 1,
    solution: [
      'Add all values: 4 + 7 + 7 + 9 + 13 = 40',
      'Divide by the number of values: 40 ÷ 5 = 8',
      'Mean = 8'
    ]
  },
  {
    id: 12,
    type: 'multiple-choice',
    points: 2,
    topic: 'Quadratic Formula',
    standard: 'A-REI.B.4',
    question: 'Using the quadratic formula, what are the solutions to x² + 4x − 5 = 0?',
    options: ['x = 1 or x = −5', 'x = −1 or x = 5', 'x = 5 or x = −1', 'x = 2 or x = −5'],
    correctAnswer: 0,
    solution: [
      'Identify a = 1, b = 4, c = −5',
      'Quadratic formula: x = [−b ± √(b² − 4ac)] / 2a',
      'Discriminant: b² − 4ac = 16 − 4(1)(−5) = 16 + 20 = 36',
      'x = (−4 ± √36) / 2 = (−4 ± 6) / 2',
      'x = (−4 + 6)/2 = 1 or x = (−4 − 6)/2 = −5'
    ]
  },
  {
    id: 13,
    type: 'multiple-choice',
    points: 2,
    topic: 'Function Notation',
    standard: 'F-IF.A.1',
    question: 'Which set of ordered pairs does NOT represent a function?',
    options: ['{(1,2),(2,3),(3,4)}', '{(1,2),(1,3),(2,4)}', '{(0,0),(1,1),(2,2)}', '{(−1,1),(0,0),(1,1)}'],
    correctAnswer: 1,
    solution: [
      'A function assigns exactly one output to each input',
      'Check {(1,2),(1,3),(2,4)}: the input x = 1 maps to both y = 2 and y = 3',
      'This violates the definition of a function',
      'Answer: {(1,2),(1,3),(2,4)} is NOT a function'
    ]
  },
  {
    id: 14,
    type: 'multiple-choice',
    points: 2,
    topic: 'Linear Models',
    standard: 'S-ID.C.7',
    question: 'A line of best fit is y = 3x + 5. What does the slope 3 represent?',
    options: [
      'The y-intercept of the data',
      'The rate of change: y increases by 3 for each 1-unit increase in x',
      'The starting value when x = 0',
      'The average value of y'
    ],
    correctAnswer: 1,
    solution: [
      'In y = mx + b, m is the slope and b is the y-intercept',
      'The slope represents the rate of change',
      'A slope of 3 means: for every 1-unit increase in x, y increases by 3',
      'Answer: The rate of change'
    ]
  },
  {
    id: 15,
    type: 'multiple-choice',
    points: 2,
    topic: 'Completing the Square',
    standard: 'A-SSE.B.3',
    question: 'Which is equivalent to x² + 6x + 2 written in vertex form?',
    options: ['(x + 3)² − 7', '(x + 6)² + 2', '(x + 3)² + 2', '(x − 3)² − 7'],
    correctAnswer: 0,
    solution: [
      'Take half of 6: 6/2 = 3. Square it: 3² = 9',
      'Add and subtract 9: x² + 6x + 9 − 9 + 2',
      'Group the perfect square: (x + 3)² − 9 + 2',
      'Simplify: (x + 3)² − 7'
    ]
  },
  {
    id: 16,
    type: 'multiple-choice',
    points: 2,
    topic: 'Domain and Range',
    standard: 'F-IF.B.5',
    question: 'What is the range of f(x) = x² + 1?',
    options: ['All real numbers', 'y ≥ 0', 'y ≥ 1', 'y > 1'],
    correctAnswer: 2,
    solution: [
      'x² is always ≥ 0 for all real x',
      'Therefore x² + 1 ≥ 0 + 1 = 1',
      'The minimum value is 1 (when x = 0)',
      'Range: y ≥ 1'
    ]
  },
  {
    id: 17,
    type: 'multiple-choice',
    points: 2,
    topic: 'Scatter Plots',
    standard: 'S-ID.B.6',
    question: 'A scatter plot shows that as x increases, y generally decreases. This is called:',
    options: ['Positive correlation', 'Negative correlation', 'No correlation', 'Perfect correlation'],
    correctAnswer: 1,
    solution: [
      'When x increases and y decreases, the variables move in opposite directions',
      'This is called a negative correlation',
      'The line of best fit would have a negative slope',
      'Answer: Negative correlation'
    ]
  },
  {
    id: 18,
    type: 'multiple-choice',
    points: 2,
    topic: 'Irrational Numbers',
    standard: 'N-RN.B.3',
    question: 'Which sum is irrational?',
    options: ['√4 + √9', '√2 + √8', '1/2 + 3/4', '√16 + 5'],
    correctAnswer: 1,
    solution: [
      '√4 + √9 = 2 + 3 = 5 (rational)',
      '√2 + √8 = √2 + 2√2 = 3√2 (irrational, since √2 is irrational)',
      '1/2 + 3/4 = 5/4 (rational)',
      '√16 + 5 = 4 + 5 = 9 (rational)',
      'Answer: √2 + √8 = 3√2 is irrational'
    ]
  },
  {
    id: 19,
    type: 'multiple-choice',
    points: 2,
    topic: 'Creating Equations',
    standard: 'A-CED.A.1',
    question: 'A plumber charges $75 for the first hour and $50 for each additional hour. Which equation gives the total cost C for h hours (h ≥ 1)?',
    options: ['C = 75h', 'C = 50h + 75', 'C = 75 + 50(h − 1)', 'C = 125h'],
    correctAnswer: 2,
    solution: [
      'First hour costs $75 (flat fee)',
      'Additional hours: (h − 1) hours at $50 each',
      'Total: C = 75 + 50(h − 1)',
      'Check h = 1: C = 75 + 50(0) = $75 ✓',
      'Check h = 2: C = 75 + 50(1) = $125 ✓'
    ]
  },
  {
    id: 20,
    type: 'multiple-choice',
    points: 2,
    topic: 'Transformations of Functions',
    standard: 'F-BF.B.3',
    question: 'How does the graph of f(x) = x² + 3 compare to f(x) = x²?',
    options: [
      'Shifted 3 units to the right',
      'Shifted 3 units upward',
      'Shifted 3 units to the left',
      'Stretched vertically by 3'
    ],
    correctAnswer: 1,
    solution: [
      'f(x) = x² + k shifts the parabola k units vertically',
      'When k > 0, the shift is upward',
      'f(x) = x² + 3 shifts the graph 3 units upward',
      'The vertex moves from (0, 0) to (0, 3)'
    ]
  },
  {
    id: 21,
    type: 'multiple-choice',
    points: 2,
    topic: 'Difference of Squares',
    standard: 'A-SSE.A.2',
    question: 'Which is the factored form of 4x² − 49?',
    options: ['(2x − 7)(2x − 7)', '(4x − 7)(x + 7)', '(2x + 7)(2x − 7)', '(2x − 49)(x + 1)'],
    correctAnswer: 2,
    solution: [
      'Recognize the pattern: a² − b² = (a + b)(a − b)',
      '4x² = (2x)² and 49 = 7²',
      'Apply the formula: (2x + 7)(2x − 7)',
      'Check: (2x + 7)(2x − 7) = 4x² − 14x + 14x − 49 = 4x² − 49 ✓'
    ]
  },
  {
    id: 22,
    type: 'multiple-choice',
    points: 2,
    topic: 'Arithmetic Sequences',
    standard: 'F-LE.A.2',
    question: 'The sequence 5, 8, 11, 14, ... is arithmetic. What is the 10th term?',
    options: ['29', '32', '35', '38'],
    correctAnswer: 1,
    solution: [
      'Identify the first term: a₁ = 5',
      'Identify the common difference: d = 8 − 5 = 3',
      'Use the formula: aₙ = a₁ + (n − 1)d',
      'a₁₀ = 5 + (10 − 1)(3) = 5 + 27 = 32'
    ]
  },
  {
    id: 23,
    type: 'multiple-choice',
    points: 2,
    topic: 'Graphing Linear Equations',
    standard: 'A-REI.D.10',
    question: 'What is the y-intercept of the line 3x − 4y = 12?',
    options: ['(0, 3)', '(0, −3)', '(4, 0)', '(0, 12)'],
    correctAnswer: 1,
    solution: [
      'Set x = 0 to find the y-intercept',
      '3(0) − 4y = 12',
      '−4y = 12',
      'y = −3',
      'y-intercept: (0, −3)'
    ]
  },
  {
    id: 24,
    type: 'multiple-choice',
    points: 2,
    topic: 'Quadratic Functions',
    standard: 'F-IF.C.7',
    question: 'What are the x-intercepts (zeros) of f(x) = x² − 4x − 12?',
    options: ['x = 6 and x = −2', 'x = −6 and x = 2', 'x = 4 and x = −3', 'x = 3 and x = −4'],
    correctAnswer: 0,
    solution: [
      'Set f(x) = 0: x² − 4x − 12 = 0',
      'Factor: find two numbers that multiply to −12 and add to −4',
      'Those numbers are −6 and 2: (x − 6)(x + 2) = 0',
      'x − 6 = 0 → x = 6 or x + 2 = 0 → x = −2'
    ]
  },
  {
    id: 25,
    type: 'multiple-choice',
    points: 2,
    topic: 'Two-Way Tables',
    standard: 'S-ID.B.5',
    question: 'In a survey, 40 students prefer math and 60 prefer English. Of the math students, 25 are girls. What fraction of math students are girls?',
    options: ['25/100', '25/40', '25/60', '15/40'],
    correctAnswer: 1,
    solution: [
      'Total math students = 40',
      'Girls who prefer math = 25',
      'Fraction = 25/40 = 5/8',
      'Answer: 25/40'
    ]
  },
  {
    id: 26,
    type: 'multiple-choice',
    points: 2,
    topic: 'Polynomial Multiplication',
    standard: 'A-APR.A.1',
    question: 'What is the product of (x + 4)(x − 3)?',
    options: ['x² + x − 12', 'x² − x − 12', 'x² + 7x − 12', 'x² − 7x + 12'],
    correctAnswer: 0,
    solution: [
      'Use FOIL: First + Outer + Inner + Last',
      'First: x · x = x²',
      'Outer: x · (−3) = −3x',
      'Inner: 4 · x = 4x',
      'Last: 4 · (−3) = −12',
      'Combine: x² − 3x + 4x − 12 = x² + x − 12'
    ]
  },
  {
    id: 27,
    type: 'multiple-choice',
    points: 2,
    topic: 'Exponential Growth vs Linear',
    standard: 'F-LE.A.3',
    question: 'Which table represents exponential growth?',
    options: [
      'x: 0,1,2,3 → y: 2,4,6,8',
      'x: 0,1,2,3 → y: 1,3,9,27',
      'x: 0,1,2,3 → y: 5,10,15,20',
      'x: 0,1,2,3 → y: 0,1,4,9'
    ],
    correctAnswer: 1,
    solution: [
      'Linear growth adds a constant amount each step',
      'Exponential growth multiplies by a constant factor each step',
      'Check option B: 1, 3, 9, 27 — each term is multiplied by 3',
      '1 × 3 = 3, 3 × 3 = 9, 9 × 3 = 27 ✓ This is exponential growth'
    ]
  },
  {
    id: 28,
    type: 'multiple-choice',
    points: 2,
    topic: 'Solving Systems by Elimination',
    standard: 'A-REI.C.5',
    question: 'Solve the system: 2x + y = 8 and x − y = 1. What is x?',
    options: ['x = 2', 'x = 3', 'x = 4', 'x = 5'],
    correctAnswer: 1,
    solution: [
      'Add the two equations to eliminate y:',
      '(2x + y) + (x − y) = 8 + 1',
      '3x = 9',
      'x = 3'
    ]
  },
  {
    id: 29,
    type: 'multiple-choice',
    points: 2,
    topic: 'Correlation',
    standard: 'S-ID.C.8',
    question: 'A correlation coefficient of r = −0.92 indicates:',
    options: [
      'A weak positive relationship',
      'A strong positive relationship',
      'A strong negative relationship',
      'No relationship'
    ],
    correctAnswer: 2,
    solution: [
      'r ranges from −1 to 1',
      'Values close to −1 indicate a strong negative relationship',
      'Values close to +1 indicate a strong positive relationship',
      'r = −0.92 is close to −1, so it is a strong negative relationship'
    ]
  },
  {
    id: 30,
    type: 'multiple-choice',
    points: 2,
    topic: 'Quadratic Formula',
    standard: 'A-REI.B.4',
    question: 'What is the discriminant of 2x² − 4x + 5 = 0, and what does it tell you?',
    options: [
      'b² − 4ac = −24; no real solutions',
      'b² − 4ac = 56; two real solutions',
      'b² − 4ac = 0; one real solution',
      'b² − 4ac = 24; two real solutions'
    ],
    correctAnswer: 0,
    solution: [
      'Identify a = 2, b = −4, c = 5',
      'Discriminant = b² − 4ac = (−4)² − 4(2)(5)',
      '= 16 − 40 = −24',
      'Since the discriminant is negative, there are no real solutions'
    ]
  }
];
